import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import {
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { useDispatch } from 'react-redux';
import { userPool } from './cognitoConfig';
import { apiFetch } from '../store/services/api';
import { identifyUser, resetUser } from '../analytics/posthog';

/**
 * Tiny sessionStorage "hint" from the previous session so UI can decide —
 * BEFORE /api/auth/me responds — whether to render optimistically or wait:
 *
 *   - Hint says super-admin or enforcement off → render everything immediately.
 *     (No flash: they'd be allowed anyway.)
 *   - Hint says restricted + enforcement on → hide restricted items until
 *     permissions load. (No flash: items appear in their final state.)
 *   - No hint (first login of session) → optimistic render, accept a small
 *     flash that one time. Hint is written after /me resolves.
 *
 * Stored data is non-sensitive (just two bools keyed by email). Server remains
 * the source of truth — the hint only controls rendering timing.
 */
const PERMISSIONS_HINT_KEY = 'mavi:auth:hint';

interface PermissionsHint {
  email: string;
  isSuperAdmin: boolean;
  enforcementEnabled: boolean;
}

export function readPermissionsHint(email: string | undefined | null): PermissionsHint | null {
  if (!email) return null;
  try {
    const raw = sessionStorage.getItem(PERMISSIONS_HINT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PermissionsHint;
    if (parsed?.email !== email) return null;
    return parsed;
  } catch { return null; }
}

function writePermissionsHint(email: string, p: AuthPermissions): void {
  // Only a session past the emailed code may skip the wait on its next load: a login that
  // still owes the code must see nothing before /mfa (2026-10-14, when the hint started
  // surviving reloads).
  if (p.mfa.required && !p.mfa.verified) {
    clearPermissionsHint();
    return;
  }
  try {
    sessionStorage.setItem(PERMISSIONS_HINT_KEY, JSON.stringify({
      email,
      isSuperAdmin: p.isSuperAdmin,
      enforcementEnabled: p.enforcementEnabled,
    }));
  } catch { /* ignore storage errors */ }
}

function clearPermissionsHint(): void {
  try { sessionStorage.removeItem(PERMISSIONS_HINT_KEY); } catch { /* ignore */ }
}

async function fetchPermissions(): Promise<AuthPermissions | null> {
  try {
    // /api/auth/me lives outside any environment-specific DB, but apiFetch always
    // attaches X-Environment. Use 'production' as a safe default — the endpoint
    // ignores it.
    const res = await apiFetch('/api/auth/me', { environment: 'production' as any });
    const data = res?.data;
    if (!data) return null;
    return {
      isSuperAdmin: !!data.isSuperAdmin,
      roleName: data.role?.name ?? null,
      roleDescription: data.role?.description ?? null,
      allowedPages: Array.isArray(data.allowedPages) ? data.allowedPages : [],
      enforcementEnabled: !!data.enforcementEnabled,
      maintenanceMode: !!data.maintenanceMode,
      platformOwnerName: typeof data.platformOwnerName === 'string' ? data.platformOwnerName : null,
      canSeeEngineering: data.canSeeEngineering === true,
      mfa: {
        required: !!data.mfa?.required,
        verified: data.mfa ? !!data.mfa.verified : true,
        revoked: !!data.mfa?.revoked,
        sessionId: data.mfa?.sessionId ?? null,
      },
    };
  } catch {
    // Backend not reachable or /me not deployed yet — fail open rather than blocking login.
    return null;
  }
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AuthUser {
  sub: string;
  email: string;
  name: string;
}

export interface AuthPermissions {
  isSuperAdmin: boolean;
  roleName: string | null;
  roleDescription: string | null;
  allowedPages: string[];
  enforcementEnabled: boolean;
  maintenanceMode: boolean;
  /**
   * Canonical Platform Specialist owner name for this user (resolved
   * server-side via OWNER_EMAIL_MAP in server/src/config/platformOwners.ts).
   * Null when the email isn't mapped. Used by the Platform Dashboard
   * filter bar to surface "their own name" in the Specialist dropdown
   * for users who lack the `platform.dashboard.all_specialists`
   * capability — replaces the brittle Cognito-display-name match.
   */
  platformOwnerName: string | null;
  /** The Engineering card and pages: an email allowlist on the server (config/engineering.ts), not a role. */
  canSeeEngineering: boolean;
  /** Session monitor + email OTP (2026-09-15): `required && !verified` → the app routes to /mfa. */
  mfa: { required: boolean; verified: boolean; revoked: boolean; sessionId: string | null };
}

interface AuthContextValue {
  user: AuthUser | null;
  permissions: AuthPermissions | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  permissionsLoading: boolean;
  needsNewPassword: boolean;
  /**
   * The API said an admin revoked this login, so the browser dropped it (2026-10-09). The login
   * page explains why; cleared by the next sign-in.
   */
  sessionRevoked: boolean;
  /** /api/auth/me has answered for the current login (and it was not revoked). */
  permissionsReady: boolean;

  signIn: (email: string, password: string) => Promise<void>;
  completeNewPassword: (newPassword: string) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  confirmPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  signOut: () => void;
  /** Re-read /api/auth/me — used after the email code is verified so the MFA gate lifts without a reload. */
  refreshPermissions: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// ---------------------------------------------------------------------------
// Helper – extract user attributes from session
// ---------------------------------------------------------------------------

function extractUser(session: CognitoUserSession, cognitoUser: CognitoUser): Promise<AuthUser> {
  return new Promise((resolve) => {
    cognitoUser.getUserAttributes((err, attrs) => {
      if (err) {
        const payload = session.getIdToken().decodePayload();
        resolve({
          sub: payload.sub as string,
          email: (payload.email as string) || '',
          name: [payload.name, payload.family_name].filter(Boolean).join(' ') || '',
        });
        return;
      }
      const map: Record<string, string> = {};
      (attrs || []).forEach((a) => { map[a.Name] = a.Value; });
      resolve({
        sub: map.sub || '',
        email: map.email || '',
        name: [map.name, map.family_name].filter(Boolean).join(' ') || '',
      });
    });
  });
}

/**
 * The signed-in user on a page load, from the ID token the restored session already holds:
 * no round trip to Cognito before the app can render (2026-10-14). The attribute call stays
 * the fallback for a token without the email, the name or the family name.
 */
function userFromSession(session: CognitoUserSession, cognitoUser: CognitoUser): Promise<AuthUser> {
  const payload = session.getIdToken().decodePayload();
  if (payload.sub && payload.email && payload.name && payload.family_name) {
    return Promise.resolve({
      sub: payload.sub as string,
      email: payload.email as string,
      name: `${payload.name} ${payload.family_name}`,
    });
  }
  return extractUser(session, cognitoUser);
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [permissions, setPermissions] = useState<AuthPermissions | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [needsNewPassword, setNeedsNewPassword] = useState(false);
  const [sessionRevoked, setSessionRevoked] = useState(false);
  const [checkedUser, setCheckedUser] = useState<AuthUser | null>(null);
  const dispatch = useDispatch();

  // Ref to hold the CognitoUser during the newPasswordRequired challenge
  const challengeUserRef = useRef<CognitoUser | null>(null);
  const challengeAttrsRef = useRef<any>(null);

  // Drop the Cognito session from this browser. `revoked`: the API said an admin revoked it.
  // A revoked login used to stay signed in here, so /login saw an authenticated user and sent
  // it back to /, which sent it back to /login, ~65 times a second (2026-10-09).
  const endSession = useCallback((revoked: boolean) => {
    resetUser();

    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) {
      cognitoUser.signOut();
    }
    challengeUserRef.current = null;
    challengeAttrsRef.current = null;
    setNeedsNewPassword(false);
    setSessionRevoked(revoked);
    setUser(null);
  }, []);

  // Sync user into Redux whenever it changes
  useEffect(() => {
    if (user) {
      dispatch({ type: 'SET_USER', payload: user });
    } else {
      dispatch({ type: 'CLEAR_USER' });
    }
  }, [user, dispatch]);

  // Fetch permissions from the backend whenever we have a user. Writes a
  // non-sensitive hint to sessionStorage so the next render can decide up
  // front whether to render restricted items optimistically or wait.
  useEffect(() => {
    if (!user) {
      setPermissions(null);
      setPermissionsLoading(false);
      return;
    }
    setSessionRevoked(false); // a new login replaces the one an admin revoked
    let cancelled = false;
    setPermissionsLoading(true);
    fetchPermissions().then((p) => {
      if (cancelled) return;
      if (p?.mfa.revoked) { endSession(true); return; }
      setPermissions(p);
      if (p) writePermissionsHint(user.email, p);
      setPermissionsLoading(false);
      setCheckedUser(user);
    });
    return () => { cancelled = true; };
  }, [user, endSession]);

  // Forget the hint once there is definitely no user (signed out, no session). Not while the
  // session is still being restored: clearing it then, as this used to, meant no reload ever
  // found it and everyone waited for /api/auth/me on every page load (2026-10-14).
  useEffect(() => {
    if (!isLoading && !user) clearPermissionsHint();
  }, [isLoading, user]);

  // On mount, check for existing session
  useEffect(() => {
    const cognitoUser = userPool.getCurrentUser();
    if (!cognitoUser) {
      setIsLoading(false);
      return;
    }

    cognitoUser.getSession(async (err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) {
        setIsLoading(false);
        return;
      }
      try {
        const u = await userFromSession(session, cognitoUser);
        identifyUser(u);
        setUser(u);
      } catch {
        // ignore – user stays null
      }
      setIsLoading(false);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ------------------------------- sign in ---------------------------------

  const signIn = useCallback(async (email: string, password: string) => {
    const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
    const authDetails = new AuthenticationDetails({ Username: email, Password: password });

    return new Promise<void>((resolve, reject) => {
      cognitoUser.authenticateUser(authDetails, {
        onSuccess: async (session) => {
          try {
            const u = await extractUser(session, cognitoUser);
            identifyUser(u);
            setUser(u);
            resolve();
          } catch (e) {
            reject(e);
          }
        },
        onFailure: (err) => reject(err),
        newPasswordRequired: (userAttributes) => {
          // Store the user so completeNewPassword can finish the challenge
          challengeUserRef.current = cognitoUser;
          challengeAttrsRef.current = userAttributes;
          setNeedsNewPassword(true);
          resolve(); // resolve without error — the UI will show the new-password form
        },
      });
    });
  }, []);

  // ------------------------- complete new password --------------------------

  const completeNewPassword = useCallback(async (newPassword: string) => {
    const cognitoUser = challengeUserRef.current;
    if (!cognitoUser) {
      throw new Error('No pending password challenge');
    }

    // Pass empty object — the admin already set all required attributes
    // when creating the user, so there's nothing extra to send back.
    return new Promise<void>((resolve, reject) => {
      cognitoUser.completeNewPasswordChallenge(newPassword, {}, {
        onSuccess: async (session) => {
          challengeUserRef.current = null;
          challengeAttrsRef.current = null;
          setNeedsNewPassword(false);
          try {
            const u = await extractUser(session, cognitoUser);
            identifyUser(u);
            setUser(u);
            resolve();
          } catch (e) {
            reject(e);
          }
        },
        onFailure: (err) => reject(err),
      });
    });
  }, []);

  // ----------------------------- forgot password ---------------------------

  const forgotPassword = useCallback(async (email: string) => {
    const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });

    return new Promise<void>((resolve, reject) => {
      cognitoUser.forgotPassword({
        onSuccess: () => resolve(),
        onFailure: (err) => reject(err),
      });
    });
  }, []);

  // ----------------------------- confirm password --------------------------

  const confirmPassword = useCallback(async (email: string, code: string, newPassword: string) => {
    const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });

    return new Promise<void>((resolve, reject) => {
      cognitoUser.confirmPassword(code, newPassword, {
        onSuccess: () => resolve(),
        onFailure: (err) => reject(err),
      });
    });
  }, []);

  // ------------------------------- sign out --------------------------------

  const signOut = useCallback(() => endSession(false), [endSession]);

  // ------------------------------- context ---------------------------------

  const refreshPermissions = useCallback(async () => {
    const p = await fetchPermissions();
    if (p?.mfa.revoked) { endSession(true); return; }
    if (p) { setPermissions(p); if (user) writePermissionsHint(user.email, p); }
  }, [user, endSession]);

  const value: AuthContextValue = {
    user,
    permissions,
    refreshPermissions,
    isAuthenticated: !!user,
    isLoading,
    permissionsLoading,
    needsNewPassword,
    sessionRevoked,
    permissionsReady: !!user && checkedUser === user,
    signIn,
    completeNewPassword,
    forgotPassword,
    confirmPassword,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
